import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PhoneCarouselContentfulModel } from '../../api/models/PhoneCarousel';
import {
  CarouselContainer,
  CarouselWrapper,
  ChevronWrapper,
  MobileDotsPhoneCarousel,
  MobileDotsPhoneCarouselContainer,
} from './phone-carousel-styles';
// >> Horizontal view, displays images side by side on a 2D rectangle that slides left/right
import PhoneCarouselHorizontalView from './PhoneCarousel.horizontal.view';
// >> Rotational view, displays images as 3D half circle that rotates
import PhoneCarouselRotationalView from './PhoneCarousel.rotational.view';
import { PhoneCarouselPhoneIndex } from './PhoneCarousel.landing';
import PhoneCarouselTextView from './PhoneCarousel.textBlock.view';

export interface PhoneCarouselWrapperProps {
  phoneItems: PhoneCarouselContentfulModel['contentfulchildren'];
  chevronColor?: string;
  mobileDots?: boolean;
  showAllCaptions?: boolean;
}

const PhoneCarouselWrapper = ({ phoneItems, chevronColor, mobileDots, showAllCaptions }: PhoneCarouselWrapperProps): JSX.Element => {
  const carouselRef = useRef<HTMLDivElement>();
  const [ carouselWidth, setCarouselWidth ] = useState<number>(null);
  const [ carouselRotationIndex, setCarouselRotationIndex ] = useState(0);
  const [ initialClientPosition, setInitialClientPosition ] = useState<{ x?: number, y?: number }>({});
  const [ finalClientPosition, setFinalClientPosition ] = useState<{ x?: number, y?: number }>({});

  // Using ES5 function as it's the recomended usage with addEventListener to correctly register removal
  function updateCarouselWidth() {
    if (carouselRef) {
      setCarouselWidth(carouselRef.current.getBoundingClientRect().width)
    }
  }

  useEffect(() => {
    updateCarouselWidth()
    window.addEventListener('resize', updateCarouselWidth);
    return () => {
      // since navigating to another page is SSR, removing event listener has no efect outside of the current page
      window.removeEventListener('resize', updateCarouselWidth);
    }
  }, [ carouselRef, updateCarouselWidth ]);

  const rotateCarouselLeft = () => {
    // If you're at the last element, reset to 0, else just increase index
    setCarouselRotationIndex(index => index === (phoneItems.length - 1) ? 0 : ++index)
  }

  const rotateCarouselRight = () => {
    // If you're at the first element, reset to last, else just reduce index
    setCarouselRotationIndex(index => index < 1 ? (phoneItems.length - 1) : --index)
  }

  const imageIndexFromCarouselRotationIndex = (index: number) => {
    if (index >= carouselRotationIndex) {
      return index - carouselRotationIndex;
    } else {
      // phoneItems is forced to be 5 items, no more no less
      // carouselRotationIndex works as a pivot reference point, where items whose index are below the pivot should be placed in order following this formula
      return (5 - carouselRotationIndex) + index;
    }
  }

  const indexedPhonesItems = phoneItems.map((phoneItem, phoneIndex) => {
    const index = imageIndexFromCarouselRotationIndex(phoneIndex);
    // >> Currently, component is forced to use 5 elements by design, constants represent order between 0 to 4 (5 items total)
    const isEdge = index === 0 || index === 4;
    const isMiddle = index === 1 || index === 3;
    const isCenter = index === 2;
    const translatePercentOffset = index < 1 ? 0 :
      index === 1 ? 30 :
        index === 2 ? 50 :
          index === 3 ? 70 :
            100;
    return {
      image: phoneItem.image,
      title: phoneItem.title,
      description: phoneItem.description,
      isEdge,
      isMiddle,
      isCenter,
      translatePercentOffset,
      index,
      phoneIndex
    } as PhoneCarouselPhoneIndex;
  });

  const handleTouchStart = useCallback((event) => {
    setInitialClientPosition({
      x: event.nativeEvent.touches[0].clientX,
      y: event.nativeEvent.touches[0].clientY
    });
  }, []);

  const handleTouchMove = useCallback((event) => {
    setFinalClientPosition({
      x: event.nativeEvent.touches[0].clientX,
      y: event.nativeEvent.touches[0].clientY
    });
  }, []);

  const handleTouchEnd = useCallback(() => {
    // checks if the carousel is on "mobile size" and if the user didn't possibly swipe up or down
    const verticalSwipeTolerance = 30;
    if (carouselWidth <= 700 && (finalClientPosition.y - initialClientPosition.y) < verticalSwipeTolerance) {
      if (finalClientPosition.x < initialClientPosition.x) {
        rotateCarouselLeft();
      } else if (finalClientPosition.x > initialClientPosition.x) {
        rotateCarouselRight();
      }

      // Tracking vertical swipe movement, if it's outside tolerance reset the clientPositions
      setInitialClientPosition({});
      setFinalClientPosition({});
    }
  }, [ carouselWidth, initialClientPosition, finalClientPosition ]);

  const shouldShowNotActiveDescriptions = useMemo(() => showAllCaptions && carouselWidth > 1100, [ showAllCaptions, carouselWidth ]);

  return (
    <>
      <CarouselWrapper>
        <ChevronWrapper orientation="left" hideOnSm={carouselRotationIndex === (indexedPhonesItems.length - 1)} onClick={rotateCarouselLeft} chevronColor={chevronColor}>
          <svg xmlns="http://www.w3.org/2000/svg" width="29" enableBackground="new 0 0 512 512" version="1.1" viewBox="0 0 512 512"><path d="m150.11 242.03 174.54-232.72c7.714-10.283 22.298-12.365 32.582-4.655 10.283 7.713 12.367 22.3 4.655 32.582l-164.07 218.76 164.07 218.77c7.713 10.282 5.627 24.871-4.655 32.582-4.186 3.14-9.086 4.656-13.945 4.656-7.076 0-14.064-3.215-18.637-9.311l-174.54-232.73c-6.205-8.274-6.205-19.653 1e-3 -27.927z" fill="#191F28" />
          </svg>
        </ChevronWrapper>
        <CarouselContainer ref={carouselRef} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
          {
            !carouselWidth ? null :
            // 700px is the design breakpoint, where we display rotational view if higher than 700px or horizontal if less than
              (carouselWidth > 700 ?
                <PhoneCarouselRotationalView indexedPhonesItems={indexedPhonesItems} carouselWidth={carouselWidth} showAllCaptions={shouldShowNotActiveDescriptions} />
                : <PhoneCarouselHorizontalView indexedPhonesItems={indexedPhonesItems} carouselRotationIndex={carouselRotationIndex} />
              )
          }
        </CarouselContainer>
        <ChevronWrapper orientation="right" hideOnSm={carouselRotationIndex === 0} onClick={rotateCarouselRight} chevronColor={chevronColor}>
          <svg xmlns="http://www.w3.org/2000/svg" width="29" version="1.1" x="0px" y="0px" viewBox="0 0 512 512" enableBackground="new 0 0 512 512" spacing="preserve"><path fill="#191F28" d="M361.891,242.03L187.347,9.31c-7.714-10.283-22.298-12.365-32.582-4.655 c-10.283,7.713-12.367,22.3-4.655,32.582l164.072,218.758L150.111,474.762c-7.713,10.282-5.627,24.871,4.655,32.582 c4.186,3.14,9.086,4.656,13.945,4.656c7.076,0,14.064-3.215,18.637-9.311l174.544-232.732 C368.097,261.683,368.097,250.304,361.891,242.03z" />
          </svg>
        </ChevronWrapper>
      </CarouselWrapper>
      {
        !shouldShowNotActiveDescriptions && (
          <div>
            <PhoneCarouselTextView indexedPhonesItems={indexedPhonesItems} indexOverride={carouselWidth > 700 ? null : carouselRotationIndex} />
          </div>
        )
      }
      {
        carouselWidth <= 700 && mobileDots && (
          <MobileDotsPhoneCarouselContainer>
            {
              indexedPhonesItems.map((phoneItem) => (
                <MobileDotsPhoneCarousel
                  key={phoneItem.phoneIndex}
                  isActive={carouselRotationIndex === phoneItem.phoneIndex}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setCarouselRotationIndex(phoneItem.phoneIndex)
                  }}
                />
              ))
            }
          </MobileDotsPhoneCarouselContainer>
        )
      }
    </>
  );
}

export default PhoneCarouselWrapper;
